import { Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { Menu, X } from "lucide-react";
import logo from "../assets/logo.png";

export default function Navbar() {
  const [isServicesOpen, setServicesOpen] = useState(false);
  const [isAboutOpen, setAboutOpen] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [isMobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const servicesRef = useRef(null);
  const aboutRef = useRef(null);

  // Close desktop dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
      if (aboutRef.current && !aboutRef.current.contains(e.target)) {
        setAboutOpen(false);
      }
    };
    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, []);

  // Handle scroll to change background and calculate progress
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }

      // Calculate scroll progress percentage
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight > 0) {
        const progress = (window.scrollY / docHeight) * 100;
        setScrollProgress(progress);
      } else {
        setScrollProgress(0);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const textColor = scrolled ? "text-slate-900 hover:text-blue-600 font-medium" : "text-white hover:text-blue-400 font-medium";
  const logoTextColor = scrolled ? "text-slate-900" : "text-white";

  return (
    <nav className={`py-2.5 px-4 sticky top-0 z-50 transition-all duration-300 ease-in-out ${scrolled ? "bg-transparent shadow-none" : "bg-gray-900/95 backdrop-blur-md shadow-md"}`}>
      <div className="max-w-7xl mx-auto flex justify-between items-center relative">
        
      <Link to="/" className="group relative flex items-center ml-4">
  {/* Logo */}
  <img src={logo} alt="Atronm Logo" className="h-10 w-auto transition-all duration-300" loading="eager" fetchpriority="high" />

  {/* ATRONM text with fade + slide-up */}
  <span className={`
    absolute bottom-1 left-[40%] -translate-x-1/2
    translate-y-4 opacity-0
    group-hover:translate-y-full group-hover:opacity-100
    transition-all duration-500 ease-out
    text-xs font-bold ${logoTextColor}
  `}>
    ATRONM
  </span>
</Link>

        {/* Desktop Menu */}
        <div className="hidden md:flex space-x-6 relative items-center">
          <Link to="/" className={textColor}>Home</Link>

          {/* Services Dropdown */}
          <div className="relative" ref={servicesRef}>
            <button
              className={`${textColor} flex items-center gap-1 focus:outline-none`}
              onClick={() => { setServicesOpen(!isServicesOpen); setAboutOpen(false); }}
              onMouseEnter={() => { setServicesOpen(true); setAboutOpen(false); }}
            >
              Services <span className="text-[10px]">▼</span>
            </button>

            {isServicesOpen && (
              <div
                className="absolute top-8 left-0 bg-white text-gray-800 rounded shadow-lg py-2 w-48 z-50"
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}
              >
                <Link to="/services/ai" className="block px-4 py-2 hover:bg-gray-100" onClick={() => setServicesOpen(false)}>AI and Machine Learning</Link>
                <Link to="/services/web" className="block px-4 py-2 hover:bg-gray-100" onClick={() => setServicesOpen(false)}>Web Development</Link>
                <Link to="/services/cloud" className="block px-4 py-2 hover:bg-gray-100" onClick={() => setServicesOpen(false)}>Cloud & DevOps</Link>
                <Link to="/services/custom" className="block px-4 py-2 hover:bg-gray-100" onClick={() => setServicesOpen(false)}>Custom Software</Link>
                <Link to="/services/app" className="block px-4 py-2 hover:bg-gray-100" onClick={() => setServicesOpen(false)}>App Development</Link>
              </div>
            )}
          </div>

          {/* About Dropdown */}
          <div className="relative" ref={aboutRef}>
            <button
              className={`${textColor} flex items-center gap-1 focus:outline-none`}
              onClick={() => { setAboutOpen(!isAboutOpen); setServicesOpen(false); }}
              onMouseEnter={() => { setAboutOpen(true); setServicesOpen(false); }}
            >
              About <span className="text-[10px]">▼</span>
            </button>

            {isAboutOpen && (
              <div
                className="absolute top-8 left-0 bg-white text-gray-800 rounded shadow-lg py-2 w-48 z-50"
                onMouseEnter={() => setAboutOpen(true)}
                onMouseLeave={() => setAboutOpen(false)}
              >
                <Link to="/about" className="block px-4 py-2 hover:bg-gray-100" onClick={() => setAboutOpen(false)}>About Us</Link>
                <Link to="/team" className="block px-4 py-2 hover:bg-gray-100" onClick={() => setAboutOpen(false)}>Our Team</Link>
              </div>
            )}
          </div>

          <Link to="/careers" className={textColor}>Careers</Link>
          <Link to="/portfolio" className={textColor}>Portfolio</Link>
          <Link to="/contact" className={textColor}>Contact</Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className={`md:hidden mr-4 ${scrolled ? "text-slate-900" : "text-white"}`}
          onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-gray-800 p-4 space-y-4">
          <Link to="/" className="block hover:text-blue-400 text-white" onClick={() => setMobileMenuOpen(false)}>Home</Link>

          {/* Mobile Services Dropdown */}
          <div className="space-y-2 text-white">
            <button
              className="w-full text-left hover:text-blue-400 flex justify-between items-center focus:outline-none"
              onClick={() => setMobileServicesOpen(!isMobileServicesOpen)}
            >
              <span>Services</span>
              <span className="text-xs">▼</span>
            </button>
            {isMobileServicesOpen && (
              <div className="pl-4 space-y-2 border-l border-gray-700 ml-1">
                <Link to="/services/ai" className="block hover:text-blue-400" onClick={() => setMobileMenuOpen(false)}>AI and Machine Learning</Link>
                <Link to="/services/web" className="block hover:text-blue-400" onClick={() => setMobileMenuOpen(false)}>Web Development</Link>
                <Link to="/services/cloud" className="block hover:text-blue-400" onClick={() => setMobileMenuOpen(false)}>Cloud & DevOps</Link>
                <Link to="/services/custom" className="block hover:text-blue-400" onClick={() => setMobileMenuOpen(false)}>Custom Software</Link>
                <Link to="/services/app" className="block hover:text-blue-400" onClick={() => setMobileMenuOpen(false)}>App Development</Link>
              </div>
            )}
          </div>

          {/* Mobile About Dropdown */}
          <div className="space-y-2 text-white">
            <button
              className="w-full text-left hover:text-blue-400 flex justify-between items-center focus:outline-none"
              onClick={() => setMobileAboutOpen(!isMobileAboutOpen)}
            >
              <span>About</span>
              <span className="text-xs">▼</span>
            </button>
            {isMobileAboutOpen && (
              <div className="pl-4 space-y-2 border-l border-gray-700 ml-1">
                <Link to="/about" className="block hover:text-blue-400" onClick={() => setMobileMenuOpen(false)}>About Us</Link>
                <Link to="/team" className="block hover:text-blue-400" onClick={() => setMobileMenuOpen(false)}>Our Team</Link>
              </div>
            )}
          </div>

          <Link to="/careers" className="block hover:text-blue-400 text-white" onClick={() => setMobileMenuOpen(false)}>Careers</Link>
          <Link to="/portfolio" className="block hover:text-blue-400 text-white" onClick={() => setMobileMenuOpen(false)}>Portfolio</Link>
          <Link to="/contact" className="block hover:text-blue-400 text-white" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
        </div>
      )}
      {/* Scroll Progress Indicator */}
      <div 
        className="absolute bottom-0 left-0 h-[3px] bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 transition-all duration-75"
        style={{ width: `${scrollProgress}%` }}
      />
    </nav>
  );
}
